import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, ITableColumn } from "../../../../components";
import DataTable from "../../../../components/components/Table/DataTable";
import { PolicyLocationReadDto } from "../../../model/PolicyLocationReadDto";
import CustomerPolicyLocationForm from "./CustomerPolicyLocationForm";
import CustomerPolicyLocationService from "./CustomerPolicyLocationService";
import CustomerDetailService from "../../../customer-details/CustomerDetailService";
import { RootState } from "../../../../shared/store";
import { updateCustomers, updateLocationDetails, updateSelectedPolicyDetails } from "../../../customer-slice/Customer-slice";
import { uuid } from "../../../../shared/util/uuid";
import { CommonUtility } from "../../../../Services/CommonUtility";
import { useConfirmationModalContext } from "../../../../shared/components/ModalConfirmationContext";
import { toast } from "react-toastify";
import { CustomerPolicyDetails as cstPolicyDetails, CS2_AttachTypes, CUSTOMER_UPDATE_BUTTON_SUCCESS, CUSTOMER_DETAIL_ERROR_WHILE_LOADING_INITIAL_DATA } from "../../../../shared/util/constant";
import { CustomerPolicyDetailLabels as cstPolicyLocationLabels } from "../../../../shared/util/label";

export interface ICustomerPolicyLocationGrid {
  customerId?: string;
  policyId?: string;
  polEventId?: string;
  validationErrors?: any;  
  tabIndex?: number;
}

const CustomerPolicyLocationGrid: React.FunctionComponent<ICustomerPolicyLocationGrid> = (props) => {
  let baseTabIndex = props.tabIndex;
  const { customerId, policyId, polEventId, validationErrors } = props;
  const dispatch = useDispatch();
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [isFormSaved, setIsFormSaved] = useState(true);
  const [isUpdatingMailAddress, setIsUpdatingMailAddress] = useState(false);
  const [currentLocationData, setCurrentLocationData] = useState<PolicyLocationReadDto | null>(null);
  const customerDetail = useSelector((state: RootState) => state?.customerSlice);
  const policyDetailKey = CommonUtility.getPolicyDetailKey(policyId, polEventId);
  const policyDetails = customerDetail.selectedPolicyDetails?.[policyDetailKey];
  const [policyLocations, setPolicyLocations] = useState<PolicyLocationReadDto[]>([]);
  const customerPolicyLocationService = new CustomerPolicyLocationService();
  const modalContext = useConfirmationModalContext();
  const [locationState, setLocationState] = useState({
    isEditing: false,
    isAddingNewLocation: false
  });

  useEffect(() => {
    if (policyDetails && policyDetails.policyLineOfBusinesses) {
      const lob = policyDetails.policyLineOfBusinesses.find((l) => l.polEventId === polEventId);
      if (lob && lob.policyLocations) {
        const sortedLocations = [...lob.policyLocations].sort((a, b) => {
          if (a.locationNumber != null && b.locationNumber != null) {
            return Number(a.locationNumber) - Number(b.locationNumber);
          }
          if (a.locationNumber == null) return 1;
          if (b.locationNumber == null) return -1;
          return String(a.locationNumber).localeCompare(String(b.locationNumber));
        });

        setPolicyLocations(sortedLocations);
        setCurrentLocationData(sortedLocations[0] || null);
        dispatch(updateLocationDetails(sortedLocations[0] || null));
      }
    }
  }, [policyId, polEventId]);

  const onRowClick = (rowData: any) => {
    if (expandedRowId === rowData.id) {
      setExpandedRowId(null);
      setCurrentLocationData(null);
      setLocationState({ isEditing: false, isAddingNewLocation: false });
    } else {
      setExpandedRowId(rowData.id);
      setCurrentLocationData(rowData);
      onLocationSelect(rowData);
      setLocationState({ isEditing: true, isAddingNewLocation: false });
    }
  };

  const onAddNewLocationClick = () => {
    // Get the correct LOB based on polEventId
    const lob = policyDetails?.policyLineOfBusinesses.find((l) => l.polEventId === polEventId);
    if (!lob) {
      console.error("LOB not found for polEventId:", polEventId);
      return;
    }

    // Calculate the next location number by finding the highest existing location number and adding 1
    // If no locations exist, default to 1
    let nextLocationNumber = 1;
    if (policyLocations && policyLocations.length > 0) {
      const maxLocationNumber = Math.max(
        ...policyLocations.map(loc => loc.locationNumber || 0)
      );
      nextLocationNumber = maxLocationNumber + 1;
    }

    const newLocation: PolicyLocationReadDto = {
      id: uuid(), // Generate a unique newid for the new location
      polId: policyId,        // Associate the location with the policy ID
      polEventId: polEventId, // Explicitly add polEventId
      lobId: lob.id,          // Include lobId from the matching LOB
      locationNumber: nextLocationNumber,   // Auto-incremented location number
      address1: "",           // Default value for address1
      address2: "",           // Default value for address2
      city: "",               // Default value for city
      state: "",              // Default value for state
      zipCode: "",
      policyHomes: [],
      policyHomeReplacementCosts: [],
      isNewUnsaved: true      // Flag to indicate this location hasn't been saved to DB yet
    };

    setPolicyLocations([newLocation, ...policyLocations]);
    onLocationSelect(newLocation);
    setExpandedRowId(newLocation.id); // Expand the newly added row
    setLocationState({ isEditing: true, isAddingNewLocation: true });
    setIsFormSaved(false);
  };

  const onUpdateFormMailAddressClick = async () => {
    if (isUpdatingMailAddress || !currentLocationData || !customerId ||
        !policyId || !polEventId || !policyDetails) {
      return;
    }

    const selectedLob = policyDetails.policyLineOfBusinesses?.find((lob) => lob.polEventId === polEventId);
    if (!selectedLob) {
      return;
    }

    setIsUpdatingMailAddress(true);
    try {
      const customerDetailService = new CustomerDetailService();
      const customer = await customerDetailService.fetchCustomerById(customerId);
      dispatch(updateCustomers(customer));

      const locations = selectedLob.policyLocations ?? [];
      const existingLocation = locations.find((location) => location.id === currentLocationData.id);
      const updatedLocation: PolicyLocationReadDto = {
        ...(existingLocation ?? currentLocationData),
        address1: customer.address1 ?? "",
        address2: customer.address2 ?? "",
        city: customer.city ?? "",
        state: customer.state ?? "",
        zipCode: customer.zipCode ?? ""
      };
      const updatedLocations = existingLocation
        ? locations.map((location) => location.id === updatedLocation.id ? updatedLocation : location)
        : [updatedLocation, ...locations];

      dispatch(updateSelectedPolicyDetails({
        policyId,
        polEventId,
        policyDetails: {
          ...policyDetails,
          policyLineOfBusinesses: policyDetails.policyLineOfBusinesses?.map((lob) =>
            lob === selectedLob ? { ...lob, policyLocations: updatedLocations } : lob
          )
        }
      }));
      setPolicyLocations((locations) =>
        locations.map((location) => location.id === updatedLocation.id ? updatedLocation : location)
      );
      onLocationSelect(updatedLocation);
      setIsFormSaved(false);
      toast.success(CUSTOMER_UPDATE_BUTTON_SUCCESS, { position: toast.POSITION.TOP_RIGHT });
    } catch (error) {
      console.error("Error updating location from customer address", error);
      toast.error(CUSTOMER_DETAIL_ERROR_WHILE_LOADING_INITIAL_DATA, { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setIsUpdatingMailAddress(false);
    }
  };

  const onRowDeleteClick = async (rowData: any, column: ITableColumn) => {
    try {
      // Prevent deletion if any Additional Interest is attached to this location
      const lob = policyDetails?.policyLineOfBusinesses?.find((l) => l.polEventId === polEventId);
      const hasAssociatedAdditionalInterests = lob?.policyAdditionalInterests?.some(
        (ai) => ai.attachTypeId?.toLowerCase() === CS2_AttachTypes.LOCATION.toLowerCase()
              && ai.attachId?.toLowerCase() === rowData.id?.toLowerCase()
      );
      if (hasAssociatedAdditionalInterests) {
        toast.error(cstPolicyDetails.LOCATION_HAS_ADDITIONAL_INTERESTS, {
          position: toast.POSITION.TOP_RIGHT
        });
        return;
      }

      // No dependencies — show confirmation and proceed with deletion
      const result = await modalContext.showConfirmation("Are you sure you want to delete this location?");
      if (!result) {
        return;
      }

      const updatedPolicyDetails = JSON.parse(JSON.stringify(policyDetails));

      if (updatedPolicyDetails && updatedPolicyDetails.policyLineOfBusinesses) {
        const updatedLOBs = updatedPolicyDetails.policyLineOfBusinesses.map((lob) => {
          if (lob.polEventId === polEventId) {
            return {
              ...lob,
              policyLocations: lob.policyLocations.filter((loc) => loc.id !== rowData.id),
            };
          }
          return lob;
        });

        updatedPolicyDetails.policyLineOfBusinesses = updatedLOBs;
        
        // Get filtered locations list
        const filteredLocations = policyLocations.filter(
          loc => loc.id !== rowData.id
        );
        setPolicyLocations(filteredLocations);
        
        // If the deleted location was the current one, select the first available location
        if (currentLocationData?.id === rowData.id) {
          if (filteredLocations.length > 0) {
            const firstLocation = filteredLocations[0];
            setCurrentLocationData(firstLocation);
            dispatch(updateLocationDetails(firstLocation));
          } else {
            setCurrentLocationData(null);
            dispatch(updateLocationDetails(null));
          }
        }

        dispatch(
          updateSelectedPolicyDetails({
            policyId: policyId,
            polEventId: polEventId,
            policyDetails: updatedPolicyDetails,
          })
        );
        setExpandedRowId(null);
        setLocationState({ isEditing: false, isAddingNewLocation: false });
        setIsFormSaved(true);
      }
    } catch (ex) {
      console.error("Error deleting location", ex);
      // Even on error, ensure UI state is reset
      setLocationState({ isEditing: false, isAddingNewLocation: false });
      setIsFormSaved(true);
    }
  };

  const onLocationSelect = (rowData: PolicyLocationReadDto) => {
    setCurrentLocationData(rowData);
    dispatch(updateLocationDetails(rowData));
  };

  const onCancelClick = () => {
    // Reset other states
    setExpandedRowId(null);
    setLocationState({ isEditing: false, isAddingNewLocation: false });
    setIsFormSaved(true);
  };

  const tableColumns: ITableColumn[] = customerPolicyLocationService.getLocationTableColumns(
    onRowClick,
    onLocationSelect,
    onCancelClick,
    onRowDeleteClick,
    currentLocationData?.id,
    baseTabIndex
  );

  const renderRow = (location: PolicyLocationReadDto, index: number) => {
    // Set tabIndex: start with 104 for index 0, then add 10 for each subsequent index
    return (
      <React.Fragment key={location.id || index}>
        {expandedRowId === location.id && (
          <tr>
            <td colSpan={tableColumns.length}>
              <CustomerPolicyLocationForm
                customerId={customerId}
                polId={policyId}
                polEventId={polEventId}
                locationId={location.id}
                tabIndex={baseTabIndex}
                policyLocation={currentLocationData!}
                setIsFormSaved={setIsFormSaved}
                formValidation={validationErrors}
              />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="customer-policy-location-grid">
      <Button className="mb-2 me-2" tabIndex={baseTabIndex} onClick={onAddNewLocationClick}>
        <i className="bi bi-plus-circle me-2"></i>
        Add Location
      </Button>
      <Button
        color="primary"
        outlined
        className="mb-2"
        style={{ minWidth: "90px" }}
        tabIndex={baseTabIndex}
        onClick={onUpdateFormMailAddressClick}
        disabled={isUpdatingMailAddress || !currentLocationData || !customerId || !policyId || !polEventId || !policyDetails}
      >
        {cstPolicyLocationLabels.UPDATE_FORM_MAIL_ADDRESS}
      </Button>

      <DataTable
        columns={tableColumns}
        data={policyLocations}
        keyColumnName="id"
        expandedRowId={expandedRowId}
        setExpandedRowId={setExpandedRowId}
        renderRow={renderRow}
        isFormSaved={isFormSaved}
        tableClassName="table-striped table-hover table-sm"
        theadClassName="tblHeader"
      />
    </div>
  );
};

export default CustomerPolicyLocationGrid;